import { Button, Tooltip } from '@civilgrid/ui/components';
import { useParams } from '@tanstack/react-router';
import { isEmpty } from 'lodash';
import { type KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { OrganizationDetail, TLoadedState, TOCChild, TOCItem, TOCParent } from '~/common/types';
import CloseIconButton from '~/components2/forms/CloseIconButton';
import Input from '~/components2/forms/Input';
import Icon from '~/components2/Icon';
import {
  FIBER_LIT_BUILDINGS_LAYER_ID,
  PROJECT_SCOPED_RENDERER_LAYER_IDS,
} from '~/components2/Map/Widgets/activeLayerIds';
import { useClickAwayListener } from '~/hooks/useClickAwayListener';
import { useSiteDataLayerEnabled } from '~/hooks/useSiteDataLayerEnabled';
import { FiberConnectbaseNote } from '~/pages/projectDetails/connectbaseStatus/FiberConnectbaseNote';
import { connectbaseLayerStatusText } from '~/pages/projectDetails/connectbaseStatus/status';
import { useConnectbaseStatus } from '~/pages/projectDetails/connectbaseStatus/useConnectbaseStatus';
import { useListGeoItems } from '~/pages/projectDetails/potholing/common';
import rootStore from '~/store';
import layerConfigStore, { isLayerConfigPending } from '~/store2/layerConfig';
import layerStore, { PINNED_LAYERS } from '~/store2/layers';
import { resolveLayerMinZoom } from '~/store2/layerZoom';
import { isProtectedLayerId, isProtectedSection } from '~/store2/protectedLayers';
import {
  computeSiteDataAvailability,
  isSiteDataAvailabilityPending,
  isSiteDataLayerId,
  isSiteDataLeafEmpty,
  isSiteDataPickerLeafHidden,
} from '~/store2/siteDataLayers';
import CustomDataTOC from './CustomDataTOC';
import GlobalCommands from './GlobalCommands';
import {
  buildBookmarkLeafIndex,
  collectLeafIds,
  handleLayerLeafToggle,
  handleLayerSectionToggle,
  handleSectionExpandToggle,
} from './handlers';
import LayerCommands from './LayerCommands';
import { LayerConfigStatusBanner } from './LayerConfigStatusBanner';
import { LayerLibraryAddLink, LayerLibraryButton } from './LayerLibraryEntry';
import { PickerCheckbox } from './PickerCheckbox';
import ProjectsLayerTOC from './ProjectsLayerTOC';
import { LAYERS_PANEL_ID, METADATA_PICKER_PANEL_ID, toPanelId } from './panelIds';
import SectionCommands from './SectionCommands';
import { handlePanelEscape, usePanelFocus } from './usePanelFocus';

export function isTOCParent(item: any): item is TOCParent {
  return item.children;
}

function matchSearch(str1: string, str2: string) {
  return str1.toLowerCase().includes(str2.toLowerCase());
}

export const TOC = () => {
  const {
    isTOCVisible,
    isMetadataPickerVisible,
    visibleLayerIds,
    pinnedLayers,
    pinnedLayerIds,
    TOCLayers,
    expandedSections,
    lockedLeafIds,
  } = useSnapshot(layerStore) as typeof layerStore;
  // Sections key expansion by document node id instead of label; catalogVersion re-renders when
  // the resolution paths arrive, and meta (the document node set) re-renders on Library
  // membership changes.
  const { loadState, catalogVersion, meta, visibleLeafIds } = useSnapshot(layerConfigStore);
  // Must mirror the handlers' loadState branch (layerConfig.ts setLayerVisible/setSectionVisible):
  // on `failed`, edits fall through to the legacy `visibleLayerIds`, so checkbox reads must too.
  const visibleLeafSource = loadState === 'loaded' ? visibleLeafIds : visibleLayerIds;
  const siteDataLayerEnabled = useSiteDataLayerEnabled();

  // Document-driven tree: once loaded, render ONLY the nodes the user's document
  // contains, labels/structure joined from the catalog (null until both arrive → the
  // static TOC stands in behind the load banner). Org special-dataset sections are not
  // document-managed and are carried over from the runtime TOC below.
  // biome-ignore lint/correctness/useExhaustiveDependencies: catalogVersion/meta are the reactive proxies for the store reads inside buildDocumentTOC
  const documentTOC = useMemo(
    () => (loadState === 'loaded' ? layerConfigStore.buildDocumentTOC() : null),
    [loadState, catalogVersion, meta],
  );
  const { inUnverifiedMode, currentOrganizationDetail, currentOrganization } = useSnapshot(rootStore);
  const { projectId, tabId } = useParams({ strict: false }) as { projectId?: string; tabId?: string };
  const fiberConnectbaseStatus = useConnectbaseStatus(projectId).status.fiberLitBuildings;
  const { current: map } = useMap();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentZoom, setCurrentZoom] = useState<number>(map?.getZoom() || 23);
  const searchTelemetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const linkedOrganizations =
    (currentOrganizationDetail as TLoadedState & OrganizationDetail)?.linkedOrganizations ?? [];
  // Load-gated: pending/empty checks must not treat an unloaded org as a pothole-log org.
  const organizationLoaded = !!currentOrganization;
  const mobileReportType = currentOrganization?.mobileReportType;

  // A Site Data leaf with zero rows of its own type has nothing to show — don't offer it in
  // the picker. Shares the `['list-geo-items', { projectId }]` react-query cache with
  // PotholesLayer/FieldNotesLayer/Legend, so this adds at most one fetch per project view
  // (only when the flag is on) rather than a duplicate one.
  const { data: siteDataGeoItems, isLoading: siteDataGeoItemsLoading } = useListGeoItems({
    projectId: projectId ?? '',
    enabled: !!projectId && siteDataLayerEnabled,
  });
  const siteDataAvailabilityPending = isSiteDataAvailabilityPending(siteDataGeoItemsLoading, organizationLoaded);
  const siteDataAvailability = useMemo(
    () => computeSiteDataAvailability(siteDataGeoItems ?? [], mobileReportType),
    [siteDataGeoItems, mobileReportType],
  );
  // Document leaves the picker hides right now, mirroring the render-time exclusions in
  // renderParent/renderChild below: protected in unverified mode, project-scoped outside a
  // project, stood-aside or empty Site Data. "Turn On All Layers" skips these so a checkbox
  // the user can't see never gets visibility persisted behind it.
  const pickerHiddenLeafIds = useMemo(() => {
    const hidden = new Set<string>();
    for (const leafId of meta?.nodeIds ?? []) {
      if (
        (inUnverifiedMode && isProtectedLayerId(leafId)) ||
        (PROJECT_SCOPED_RENDERER_LAYER_IDS.has(leafId) && !projectId) ||
        isSiteDataPickerLeafHidden(leafId, tabId, siteDataLayerEnabled) ||
        isSiteDataLeafEmpty(leafId, siteDataAvailability, organizationLoaded)
      ) {
        hidden.add(leafId);
      }
    }
    return hidden;
  }, [meta, inUnverifiedMode, projectId, tabId, siteDataLayerEnabled, siteDataAvailability, organizationLoaded]);
  // Debounced so a fast-resolving fetch/org load never flashes a spinner — only shown if still
  // resolving after 300ms, and cleared immediately once it resolves either way.
  const [siteDataLoaderVisible, setSiteDataLoaderVisible] = useState(false);
  useEffect(() => {
    if (!siteDataAvailabilityPending) {
      setSiteDataLoaderVisible(false);
      return;
    }
    const timer = setTimeout(() => setSiteDataLoaderVisible(true), 300);
    return () => clearTimeout(timer);
  }, [siteDataAvailabilityPending]);

  const onZoomEnd = (e: any) => {
    const z: number | undefined = e?.viewState?.zoom;
    z && setCurrentZoom(z);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (map) {
      map.on('zoomend', onZoomEnd);

      return () => {
        map.off('zoomend', onZoomEnd);
      };
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    setSearchTerm('');
  }, [isTOCVisible]);

  useEffect(() => {
    return () => {
      if (searchTelemetryTimerRef.current) {
        clearTimeout(searchTelemetryTimerRef.current);
      }
    };
  }, []);

  const renderParent = (item: TOCParent, ignoreFilter = false, topSectionLabel?: string) => {
    const shouldIgnoreFilter = !isEmpty(searchTerm) && matchSearch(item.label, searchTerm);
    // Carry top-level section label down so leaf checkboxes route to the right project_*_layer_toggled event.
    const effectiveTopLabel = topSectionLabel ?? item.label;
    // Exclude leaves renderChild wouldn't render (project-scoped outside a project, Site Data
    // stood aside for the owner tab / flag off, or a Site Data leaf with no data of its own
    // type) from the all-checked calculation and bulk-toggle target, so a hidden leaf can't
    // skew a section's checkbox state.
    const visibleItemChildren = item.children.filter((child) => {
      if (isTOCParent(child)) return true;
      if (PROJECT_SCOPED_RENDERER_LAYER_IDS.has(child.id) && !projectId) return false;
      if (isSiteDataPickerLeafHidden(child.id, tabId, siteDataLayerEnabled)) return false;
      if (isSiteDataLeafEmpty(child.id, siteDataAvailability, organizationLoaded)) return false;
      return true;
    });
    const children = visibleItemChildren
      .map((child) => {
        if (isTOCParent(child)) {
          return renderParent(child, ignoreFilter || shouldIgnoreFilter, effectiveTopLabel);
        } else {
          return renderChild(child, ignoreFilter || shouldIgnoreFilter, effectiveTopLabel);
        }
      })
      .filter((item) => !isEmpty(item));

    // Only LEAF nodes track visibility — a parent's checkbox is derived from its
    // descendant leaves: none visible → unchecked, some → indeterminate, all → checked.
    // A tab-locked leaf counts as visible (the tab is forcing it onto the map) regardless
    // of the persisted checkbox source.
    const sectionLeafIds = collectLeafIds(visibleItemChildren);
    const hasLockedLeaf = sectionLeafIds.some((id) => lockedLeafIds.has(id));
    const visibleLeafCount = sectionLeafIds.filter((id) => lockedLeafIds.has(id) || visibleLeafSource.has(id)).length;
    const sectionCheckedState: boolean | 'indeterminate' =
      visibleLeafCount === 0 ? false : visibleLeafCount === sectionLeafIds.length ? true : 'indeterminate';
    // A section's expansion key is its OWN catalog node id
    const legacySectionId = item.sectionId || item.label;
    const sectionKey =
      item.label !== PINNED_LAYERS
        ? item.sectionId && layerConfigStore.isCatalogParent(item.sectionId)
          ? item.sectionId
          : (layerConfigStore.resolveParentNodeId(collectLeafIds(item.children)) ?? legacySectionId)
        : legacySectionId;
    // Search forces expansion at render time — transient UI state that must never pass through
    // `expandedSections`, where the debounced sync would persist it.
    const isExpanded = expandedSections.has(sectionKey) || !isEmpty(searchTerm);
    const panelId = toPanelId('toc', sectionKey);

    // Derived from descendant leaves, not the section label: a section is blocked for
    // unverified users iff every leaf under it is. Mixed sections (e.g. Bookmarked) stay
    // enabled while their protected leaves individually disable.
    const checkIfProtectedSection = () => inUnverifiedMode && isProtectedSection(sectionLeafIds);

    const returnLayerCheckedValue = (): boolean | 'indeterminate' => {
      // in non-proprietary mode, ensure checkbox value for utility layers is false
      if (checkIfProtectedSection()) {
        return false;
      } else {
        return sectionCheckedState;
      }
    };

    const returnLayerDisabledValue = () => {
      // in non-proprietary mode, disable checkbox for utility layers
      if (checkIfProtectedSection()) {
        return inUnverifiedMode;
      }

      // A section containing a tab-locked leaf disables wholesale, so its bulk toggle
      // can't override the layers the active tab is forcing on.
      return hasLockedLeaf;
    };

    const returnLayerLabelStyle = () => {
      // in non-proprietary mode, if section is utilities or one of its children, italicize and gray out label
      if (checkIfProtectedSection()) {
        return 'italic opacity-70';
      }

      return 'opacity-100';
    };

    const returnSectionTooltipText = () => {
      if (checkIfProtectedSection()) {
        return 'Unverified users cannot access this data layer';
      }
      return 'Contains layers controlled by the active tab';
    };

    if (children.length === 0) {
      return null;
    }

    return (
      <div
        id="toc-list"
        // Key by the unique, path-encoded document node id (matches the testid). Display
        // labels can collide across sibling document-TOC sections (e.g. a `_toc:` section and
        // an `_sd:` provider sharing a label) → duplicate React keys + mis-reconciled expansion.
        // The legacy/static TOC keeps the `?? label` fallback — its sections are a fixed
        // hardcoded set with unique labels.
        key={item.sectionId ?? item.label}
        data-testid={`toc-section-${item.sectionId ?? item.label}`}
        className={'my-2 px-1'}
      >
        <div className="group flex cursor-pointer items-center justify-between space-x-2">
          <div className="flex items-center space-x-2">
            <button
              type="button"
              data-testid={`toc-section-expand-${sectionKey}`}
              className="inline-flex appearance-none border-0 bg-transparent p-0"
              onClick={() => handleSectionExpandToggle(sectionKey, isExpanded)}
              aria-label={item.label}
              aria-expanded={isExpanded}
              aria-controls={isExpanded ? panelId : undefined}
            >
              <Icon icon={isExpanded ? 'chevronDown' : 'chevronRight'} />
            </button>

            <Tooltip
              asChild
              isDisabled={!checkIfProtectedSection() && !hasLockedLeaf}
              text={returnSectionTooltipText()}
            >
              <span>
                <PickerCheckbox
                  data-testid={`toc-section-toggle-${sectionKey}`}
                  checked={returnLayerCheckedValue()}
                  disabled={returnLayerDisabledValue()}
                  onCheckedChange={(value) => handleLayerSectionToggle(item.label, value, visibleItemChildren)}
                  label={item.label}
                  labelClassName={returnLayerLabelStyle()}
                />
              </span>
            </Tooltip>
          </div>
          {!checkIfProtectedSection() && (
            <SectionCommands title={item.label} pickerHiddenLeafIds={pickerHiddenLeafIds} />
          )}
        </div>

        {isExpanded && (
          <div id={panelId} className="my-2 ml-2.5">
            {children}
          </div>
        )}
      </div>
    );
  };
  const { mouseEventRef, hasClickedAway } = useClickAwayListener();

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (hasClickedAway && isTOCVisible) {
      //layerStore.toggleTOC(); // disable clickAway temp
    }
  }, [hasClickedAway]);

  const renderChild = (item: TOCChild, ignoreFilter = false, topSectionLabel?: string) => {
    // A tab-locked leaf is being forced onto the map by the active tab: it renders checked
    // (whatever the persisted source says) and disabled, so the tab's control can't be
    // overridden from the picker until the tab unlocks it.
    const isLocked = lockedLeafIds.has(item.id);
    const isVisible = isLocked || visibleLeafSource.has(item.id);

    // Project-scoped leaves (fiberLitBuildings from ConnectBase, Site Data from the project
    // geo-items API) require a projectId. Hide them in non-project contexts so users can't
    // toggle on a layer that would error without a project.
    if (PROJECT_SCOPED_RENDERER_LAYER_IDS.has(item.id) && !projectId) {
      return null;
    }
    // On the Field Notes / Potholing tabs, the tab owns the display and forces a single
    // dataset — so the whole Site Data section stands aside in the picker (both leaves hide →
    // the parent renders nothing). Matches the render-time stand-aside in shouldRenderSiteDataLayer.
    if (isSiteDataPickerLeafHidden(item.id, tabId, siteDataLayerEnabled)) {
      return null;
    }
    // A Site Data leaf with no data of its own type in this project has nothing to show.
    if (isSiteDataLeafEmpty(item.id, siteDataAvailability, organizationLoaded)) {
      return null;
    }
    const isPinned = pinnedLayerIds.has(item.id);

    const minZoom = resolveLayerMinZoom(item.id, layerStore.tilesConfig?.[item.id]);
    const isBelowMinZoom = currentZoom < minZoom;

    const checkIfProtectedLayer = () => inUnverifiedMode && isProtectedLayerId(item.id);
    // Fiber lit buildings come from ConnectBase, so a retrying or failed task leaves the layer without data.
    const connectbaseUnavailableText =
      item.id === FIBER_LIT_BUILDINGS_LAYER_ID && fiberConnectbaseStatus
        ? connectbaseLayerStatusText(item.label, fiberConnectbaseStatus)
        : undefined;

    const returnLayerCheckedValue = () => {
      if (checkIfProtectedLayer()) {
        return false;
      }
      return isVisible;
    };

    const returnLayerDisabledValue = () => {
      if (checkIfProtectedLayer()) {
        return true;
      }
      return isLocked;
    };

    const returnLayerLabelStyle = () => {
      if (checkIfProtectedLayer()) {
        return 'italic opacity-70';
      }

      return isBelowMinZoom || connectbaseUnavailableText ? 'italic opacity-70' : 'opacity-100';
    };

    const isUtilityLayer = topSectionLabel === 'Utilities';

    const returnLayerTooltipText = () => {
      if (checkIfProtectedLayer()) {
        return 'Unverified users cannot access this data layer';
      }

      if (isLocked) {
        return 'Controlled by the active tab';
      }

      if (connectbaseUnavailableText) {
        return connectbaseUnavailableText;
      }

      if (isBelowMinZoom) {
        return 'Data not available at this zoom level. Zoom in further to view';
      }

      if (isUtilityLayer) {
        return "Additional info available within a Project's Utilities section";
      }

      return '';
    };

    const shouldShowLayerTooltip =
      checkIfProtectedLayer() || isLocked || !!connectbaseUnavailableText || isBelowMinZoom || isUtilityLayer;

    if (ignoreFilter || isEmpty(searchTerm) || (!isEmpty(searchTerm) && matchSearch(item.label, searchTerm))) {
      return (
        <div
          key={item.id}
          data-testid={`toc-layer-${item.id}`}
          className={
            // fixed height ensures no "jumping" when bookmark icon displays, makes the
            // line height just a bit bigger
            `group relative my-1 ml-7 flex min-h-[1.75rem] flex-row items-center justify-between space-x-2`
          }
        >
          <div className="grow">
            <Tooltip asChild isDisabled={!shouldShowLayerTooltip} text={returnLayerTooltipText()}>
              <span>
                <PickerCheckbox
                  data-testid={`toc-layer-toggle-${item.id}`}
                  checked={returnLayerCheckedValue()}
                  disabled={returnLayerDisabledValue()}
                  onCheckedChange={(isVisible) => {
                    if (isVisible === 'indeterminate') return;
                    handleLayerLeafToggle(item.id, isVisible);
                    if (projectId) {
                      captureTypedPosthogEvent(POSTHOG_EVENTS.projectLayerFilterChanged, {
                        project_id: projectId,
                        organization_id: rootStore.currentOrganizationId || undefined,
                        filter_type: 'layer_visibility',
                        value: item.id,
                        action: isVisible ? 'add' : 'remove',
                      });

                      if (topSectionLabel === 'Environmental') {
                        captureTypedPosthogEvent(POSTHOG_EVENTS.projectEnvironmentalLayerToggled, {
                          project_id: projectId,
                          organization_id: rootStore.currentOrganizationId || undefined,
                          layer_id: item.id,
                          layer_name: item.label,
                          enabled: isVisible,
                          source: 'toc',
                        });
                      } else if (topSectionLabel === 'Geotechnical') {
                        captureTypedPosthogEvent(POSTHOG_EVENTS.projectGeotechLayerToggled, {
                          project_id: projectId,
                          organization_id: rootStore.currentOrganizationId || undefined,
                          layer_id: item.id,
                          layer_name: item.label,
                          enabled: isVisible,
                          source: 'toc',
                        });
                      }
                    }
                  }}
                  label={item.label}
                  labelClassName={returnLayerLabelStyle()}
                />
              </span>
            </Tooltip>
            {item.id === FIBER_LIT_BUILDINGS_LAYER_ID && projectId && topSectionLabel !== PINNED_LAYERS && (
              <FiberConnectbaseNote projectId={projectId} layerName={item.label} status={fiberConnectbaseStatus} />
            )}
          </div>

          {!checkIfProtectedLayer() && <LayerCommands item={item} isPinned={isPinned} minZoom={minZoom} />}
        </div>
      );
    } else {
      return null;
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const items = useMemo(() => {
    const layerList: (JSX.Element | null)[] = [<ProjectsLayerTOC key="projects-layer-toc" />];

    // The project-scoped "Custom Data" group is not a document/catalog node, so the document tree
    // can't carry it — it renders as a separately-managed overlay section (CustomDataTOC), placed
    // at the top. Gated on `documentTOC` so the fallback `TOCLayers` below, which still contains
    // the legacy-spliced group, never double-renders it.
    if (documentTOC) {
      layerList.push(<CustomDataTOC key="custom-data-toc" searchTerm={searchTerm} />);
    }

    // Document-driven: the document tree IS the picker. Entitled special datasets are part of the
    // catalog, so an added one is reassembled into `documentTOC` like any other layer — it appears
    // in the picker iff the user added it from the Library, never auto-injected. While the
    // document is not loaded (`documentTOC` null) the static `TOCLayers` stands in.
    const sourceTOC = documentTOC ?? TOCLayers;

    sourceTOC.forEach((item) => {
      // While the data-availability check is still resolving (past the debounce delay), show a
      // placeholder for the Site Data section instead of the real one — its real leaves would
      // otherwise render as absent (availability defaults to false while loading), which would
      // then pop in once resolved. Detected structurally, not by label, to match the Library's
      // isSiteDataGroup check.
      const siteDataLeafIds = isTOCParent(item) ? collectLeafIds(item.children) : [];
      if (
        isTOCParent(item) &&
        siteDataLoaderVisible &&
        siteDataLeafIds.every(isSiteDataLayerId) &&
        siteDataLeafIds.some((id) => !isSiteDataPickerLeafHidden(id, tabId, siteDataLayerEnabled))
      ) {
        layerList.push(
          <div
            key="site-data-loading"
            data-testid="toc-section-site-data-loading"
            className="my-2 flex items-center gap-2 px-1"
          >
            <div className="h-3 w-3 animate-spin rounded-full border-2 border-grey-30 border-t-primary" />
            <span className="text-xs text-grey-60">Site Data</span>
          </div>,
        );
        return;
      }
      if (isTOCParent(item)) {
        layerList.push(renderParent(item));
      } else {
        layerList.push(renderChild(item));
      }
    });

    return layerList.filter((item) => !isEmpty(item));
  }, [
    documentTOC,
    visibleLayerIds,
    visibleLeafIds,
    lockedLeafIds,
    TOCLayers,
    searchTerm,
    expandedSections,
    pinnedLayers,
    pinnedLayerIds,
    currentZoom,
    linkedOrganizations,
    catalogVersion,
    projectId,
    tabId,
    fiberConnectbaseStatus,
    siteDataLayerEnabled,
    siteDataAvailability,
    organizationLoaded,
    siteDataLoaderVisible,
    inUnverifiedMode,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const bookmarkedLayers = useMemo(() => {
    if (pinnedLayerIds.size === 0) return null;

    const TOCItems: TOCItem[] = [];

    if (documentTOC) {
      // Document-driven: bookmarks are document leaves (a pinned id the document no longer
      // contains renders nothing) — the static-TOC pinnedLayers lookup can't see Library
      // additions like org-custom leaves. Labels are composed with the parent utility/dataset
      // prefix to match the legacy path (e.g. "Gas / Transmission Lines").
      const leafById = buildBookmarkLeafIndex(documentTOC);
      Array.from(pinnedLayerIds).forEach((id) => {
        const leaf = leafById.get(id);
        if (leaf) TOCItems.push(leaf);
      });
    } else {
      Array.from(pinnedLayerIds).forEach((id) => {
        const layer = pinnedLayers.get(id);

        if (layer) {
          TOCItems.push(layer);
        }
      });
    }

    if (TOCItems.length === 0) return null;

    const parent: TOCParent = {
      label: PINNED_LAYERS,
      expanded: true,
      children: TOCItems,
    };

    return renderParent(parent);
  }, [
    documentTOC,
    visibleLayerIds,
    visibleLeafIds,
    lockedLeafIds,
    TOCLayers,
    searchTerm,
    expandedSections,
    pinnedLayers,
    pinnedLayerIds,
    currentZoom,
    projectId,
    fiberConnectbaseStatus,
    tabId,
    catalogVersion,
    siteDataLayerEnabled,
    siteDataAvailability,
    organizationLoaded,
    inUnverifiedMode,
  ]);

  const isLoading = isLayerConfigPending(loadState);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const titleId = useId();
  usePanelFocus(LAYERS_PANEL_ID, () => (isLoading ? mouseEventRef.current : titleRef.current), !isLoading, [
    METADATA_PICKER_PANEL_ID,
  ]);
  const closeOnEscape = (e: KeyboardEvent<HTMLElement>) => handlePanelEscape(e, () => layerStore.toggleTOC());

  if (isTOCVisible && isLoading) {
    return (
      // biome-ignore lint/a11y/noNoninteractiveElementInteractions: Escape from any control inside closes the dialog
      <div
        id={LAYERS_PANEL_ID}
        role="dialog"
        aria-label="Layers"
        aria-busy="true"
        tabIndex={-1}
        onKeyDown={closeOnEscape}
        data-testid="layers-toc-loading"
        className="pointer-events-auto absolute left-4 top-4 z-20 flex w-[26rem] max-w-[80%] items-center gap-2 rounded-sm border border-grey-30 bg-white p-4 shadow-lg"
        ref={mouseEventRef}
      >
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-grey-30 border-t-primary" />
        <span className="text-sm text-grey-60">Loading layers…</span>
      </div>
    );
  }

  if (isTOCVisible) {
    return (
      // biome-ignore lint/a11y/noNoninteractiveElementInteractions: Escape from any control inside closes the dialog
      <div
        id={LAYERS_PANEL_ID}
        role="dialog"
        aria-labelledby={titleId}
        onKeyDown={closeOnEscape}
        data-testid="layers-toc"
        className="pointer-events-auto absolute left-4 top-4 z-20 flex max-h-[90%] w-[26rem] max-w-[80%] flex-col rounded-sm bg-white p-1 shadow-lg border border-grey-30"
        ref={mouseEventRef}
      >
        <div className="flex w-full items-center justify-between p-2">
          <h2 id={titleId} ref={titleRef} tabIndex={-1} className="rounded-sm text-base font-semibold focus-ring">
            Layers
          </h2>

          <div className="flex items-center space-x-2">
            <Button
              variant="secondary"
              aria-haspopup="dialog"
              aria-expanded={isMetadataPickerVisible}
              aria-controls={isMetadataPickerVisible ? METADATA_PICKER_PANEL_ID : undefined}
              data-testid="toc-metadata-button"
              onClick={() => {
                const wasOpen = layerStore.isMetadataPickerVisible;
                layerStore.toggleMetadataPicker();
                // Only fire on open; the same button also closes the picker.
                if (!wasOpen && projectId) {
                  captureTypedPosthogEvent(POSTHOG_EVENTS.projectMetadataOpened, {
                    project_id: projectId,
                    organization_id: rootStore.currentOrganizationId || undefined,
                    metadata_source: 'toc_picker',
                  });
                }
              }}
            >
              Layer Attributes
            </Button>
            <LayerLibraryButton />
            <GlobalCommands pickerHiddenLeafIds={pickerHiddenLeafIds} />
            <CloseIconButton
              aria-label="Close layers"
              data-testid="layers-close-button"
              onClick={() => layerStore.toggleTOC()}
            />
          </div>
        </div>
        <LayerConfigStatusBanner />
        <Input
          label=""
          placeholder="Search"
          data-testid="toc-search-input"
          onChange={(e) => {
            // Debounce non-empty changes (one event per query, not per keystroke); clear fires immediately.
            if (projectId) {
              if (searchTelemetryTimerRef.current) {
                clearTimeout(searchTelemetryTimerRef.current);
                searchTelemetryTimerRef.current = null;
              }
              if (e === '') {
                captureTypedPosthogEvent(POSTHOG_EVENTS.projectLayerFilterChanged, {
                  project_id: projectId,
                  organization_id: rootStore.currentOrganizationId || undefined,
                  filter_type: 'search',
                  value: null,
                  action: 'clear',
                });
              } else {
                searchTelemetryTimerRef.current = setTimeout(() => {
                  captureTypedPosthogEvent(POSTHOG_EVENTS.projectLayerFilterChanged, {
                    project_id: projectId,
                    organization_id: rootStore.currentOrganizationId || undefined,
                    filter_type: 'search',
                    value: e,
                    action: 'set',
                  });
                }, 500);
              }
            }

            setSearchTerm(e);
          }}
          value={searchTerm}
        />
        {items.length > 0 ? (
          <div className="my-1 overflow-y-auto pr-2">
            {bookmarkedLayers}
            {items}
          </div>
        ) : (
          <div>Search did not return any results.</div>
        )}
        <LayerLibraryAddLink />
      </div>
    );
  } else {
    return null;
  }
};
